import { UseFormReturn, useWatch } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { previewManagedEventTypes } from "@/features/supply-config/managed-events-api";
import { Badge } from "@/components/ui/badge";
import ThreeDotsLoader from "@/components/common/three-dots-loader";
import { ManagedEventWizardData } from "./wizard-form";

export default function StepIndividual({
  form,
}: {
  form: UseFormReturn<ManagedEventWizardData>;
}) {
  const teamIds = useWatch({ control: form.control, name: "team_ids" }) ?? [];
  const name = useWatch({ control: form.control, name: "name" });

  const { data, isLoading, error } = useQuery({
    queryKey: ["preview-event-types", teamIds, name],
    queryFn: () => previewManagedEventTypes({ team_ids: teamIds, name }),
    enabled: teamIds.length > 0 && !!name,
  });

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Individual event types will be created automatically — one per team
        professional, matched from the staged Calendly events by name + owner.
      </p>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <ThreeDotsLoader />
        </div>
      ) : error ? (
        <div className="text-sm text-red-600">Failed to load the preview.</div>
      ) : !data ? null : (
        <div className="space-y-3">
          <div className="rounded-md border border-gray-200 p-3 text-sm">
            <strong className="text-green-700">{data.matched.length}</strong> of{" "}
            {data.totalProfessionals} professionals will get an event type.
          </div>

          {data.matched.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">
                Matched ({data.matched.length})
              </p>
              <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
                {data.matched.map((m) => (
                  <Badge key={m.professional.id} variant="secondary">
                    {m.professional.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {data.unmatched.length > 0 && (
            <div>
              <p className="text-xs font-medium text-amber-600 mb-1">
                Unmatched ({data.unmatched.length}) — these won't get an event
                type
              </p>
              <div className="border border-gray-200 rounded-md divide-y max-h-40 overflow-y-auto text-xs">
                {data.unmatched.map((u) => (
                  <div
                    key={u.id}
                    className="flex items-center justify-between px-3 py-1.5"
                  >
                    <span>{u.name}</span>
                    <span className="text-gray-400">{u.reason}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
